// Helpers for the Users and Roles pages (kept out of the component files for
// the react-refresh/only-export-components lint rule).

/** The server's message for a failed request: a sentence, or the first validation error. */
export const apiError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length) {
    // FastAPI validation errors: "Value error, Use at least 6 characters."
    return String(detail[0].msg || fallback).replace(/^Value error, /, '');
  }
  if (!error?.response) return 'The POS server did not answer. Check the connection and try again.';
  return fallback;
};

export const USERNAME_PATTERN = /^[a-z0-9._-]{3,50}$/;
export const MIN_PASSWORD = 6;

/** What the sign-in page and the server accept as a username (lower-cased). */
export const cleanUsername = (value) => (value || '').trim().toLowerCase();

export const usernameProblem = (value) =>
  USERNAME_PATTERN.test(cleanUsername(value))
    ? null
    : 'Use 3 to 50 letters or numbers (dots, dashes and _ are fine), no spaces.';

export const passwordProblem = (value) =>
  (value || '').length >= MIN_PASSWORD ? null : `Use at least ${MIN_PASSWORD} characters.`;

/** A discount limit typed in a box: a whole number 0-100, or null when it isn't one. */
export const parseLimit = (value) => {
  const text = String(value ?? '').trim();
  if (!/^\d{1,3}$/.test(text)) return null;
  const n = Number(text);
  return n >= 0 && n <= 100 ? n : null;
};

/** People first by on/off, then by name. */
export const sortPeople = (people) =>
  [...people].sort(
    (a, b) =>
      Number(b.is_active) - Number(a.is_active) ||
      (a.full_name || a.username).localeCompare(b.full_name || b.username)
  );

/**
 * Tick or untick one permission. An action needs its page, so ticking an action
 * ticks the page, and unticking a page unticks its actions.
 */
export const togglePermission = (selected, key, checked, catalogue) => {
  const next = new Set(selected);
  const byKey = Object.fromEntries(catalogue.map((p) => [p.key, p]));
  if (checked) {
    next.add(key);
    if (byKey[key]?.requires) next.add(byKey[key].requires);
  } else {
    next.delete(key);
    catalogue.filter((p) => p.requires === key).forEach((p) => next.delete(p.key));
  }
  // Keep the catalogue order so the saved list reads the same every time.
  return catalogue.map((p) => p.key).filter((k) => next.has(k));
};

/** Labels of a role's permissions, pages first then actions, for the role cards. */
export const permissionLabels = (keys, catalogue) =>
  catalogue.filter((p) => keys.includes(p.key)).map((p) => p.label);
